import { ChangeEvent, useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { cpf, cnpj } from "cpf-cnpj-validator";
import axios from "../../../../services/axios";
import Constants from "../../../../constants/Index";
import { fetchStateCities, fetchStatesUF } from "../../../../services/ibge";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { ButtonV2, formatErrorMessageV2, OptionV2, RemoveButtonV2 } from "../../../../components/FormControlsV2";
import { OnboardingFooterV2 } from "../../../../components/OnboardingV2/OnboardingFooterV2";
import { FarmFormV2, FarmFormValueV2 } from "../../../../components/FormsV2";
import { fromFarmFormValue, toFarmFormValue, toUserOptions } from "../adapters/propertyForms";
import { persistPayload } from "../adapters/payload";
import { generateuuid } from "../../../../utils/uuid/generate";
import { hasErrors, validateV2, ValidationErrorsV2 } from "../adapters/validation";
import { DrawOnMapSlotV2 } from "../../../../components/OnboardingV2/DrawOnMapSlotV2";
import { SliderComponent } from "../../../../components/EditOnboarding/Components/Slider/Index";
import { readFileKML } from "../../../../utils/kml/readFile";
import { ICoords } from "../../../../@types/GoogleMaps/ICoord";

function createFarmSchema(initialsUsed: string[]) {
  return yup.object().shape({
    name: yup.string().matches(/^[^\s]+(\s+[^\s]+)*$/, "Este nome não é válido.").required("NOME DA FAZENDA é necessário."),
    initials: yup
      .string()
      .matches(/^[^\s]+(\s+[^\s]+)*$/, "Esta sigla não é válida.")
      .max(3, "tamanho deve ser entre 1 e 3")
      .required("SIGLA é necessário.")
      .notOneOf(initialsUsed, "Esta sigla já está sendo utilizada."),
    document: yup
      .string()
      .notRequired()
      .test("documentCPF", "CPF informado inválido", (value) => !value || value.length > 11 || cpf.isValid(value))
      .test("documentCNPJ", "CNPJ informado inválido", (value) => !value || value.length <= 11 || cnpj.isValid(value)),
    manager_user_id: yup.object().nullable().required("RESPONSÁVEL é necessário."),
    address: yup.object({
      state: yup.object().nullable().required("UF é necessário."),
      city: yup.object().nullable().required("CIDADE é necessário."),
    }),
    map_coords: yup.array().min(1, "Mapa da fazenda é necessário."),
  });
}

function createFarmBase(): FarmFormValueV2 {
  return {
    uuid: generateuuid(),
    name: "",
    initials: "",
    document: "",
    state_registration: "",
    manager_user_id: null,
    address: { state: null, city: null },
    season: { rain_initial: 3, dry_initial: 8 },
    map_coords: [],
  };
}

function farmViewKey(farm: FarmFormValueV2, index: number) {
  return farm.uuid || String(index);
}

function getFarmMapCoords(coords?: any): ICoords {
  if (Array.isArray(coords)) return coords as ICoords;
  if (Array.isArray(coords?.coords)) return coords.coords;

  if (typeof coords === "string") {
    try {
      return getFarmMapCoords(JSON.parse(coords));
    } catch {
      return [];
    }
  }

  return [];
}

function hasCoords(coords?: any) {
  return getFarmMapCoords(coords).length > 0;
}

export function CreateFarmsV2() {
  const {
    payload,
    setPayload,
    setBlockNextStep,
    setShowScreen,
    updateStateManually,
    userProgress,
    apiFieldErrors,
    setApiFieldErrors,
    clearStepError,
  } = useOnboarding();
  const [farms, setFarms] = useState<FarmFormValueV2[]>([createFarmBase()]);
  const [states, setStates] = useState<OptionV2[]>([]);
  const [citiesByFarm, setCitiesByFarm] = useState<Record<string, OptionV2[]>>({});
  const [errors, setErrors] = useState<Record<string, ValidationErrorsV2>>({});
  const [kmlErrors, setKmlErrors] = useState<Record<string, string>>({});
  const [kmlFileNames, setKmlFileNames] = useState<Record<string, string>>({});
  const [initialsUsed, setInitialsUsed] = useState<string[]>([]);
  const [showFullValidation, setShowFullValidation] = useState(false);
  const userProgressRef = useRef(userProgress);

  const managers = toUserOptions(payload, Constants.PROFILE_CODE_GERENTE);

  async function validateFarms(farmsToValidate = farms, fullValidation = showFullValidation) {
    const farmSchema = createFarmSchema(initialsUsed);
    const validationEntries = await Promise.all(
      farmsToValidate.map(async (farm, index) => {
        const validation = fullValidation ? await validateV2(farmSchema, farm) : {};
        const sameInitials = farmsToValidate.filter((item) => item.initials && item.initials === farm.initials).length;
        if (sameInitials > 1 || (farm.initials && initialsUsed.includes(farm.initials))) {
          validation.initials = Constants.MESSAGE_UNIQUE_INITIALS;
        }
        return [String(index), validation] as const;
      })
    );
    const validation = Object.fromEntries(validationEntries);
    setErrors(validation);
    const invalid = Object.values(validation).some(hasErrors);
    const hasApiErrors = hasFarmApiErrors(farmsToValidate);
    setBlockNextStep(invalid || hasApiErrors);
    if (!invalid && !hasApiErrors) {
      clearStepError(1, 3);
    }
    return !invalid && !hasApiErrors;
  }

  function updateFarm(index: number, value: FarmFormValueV2) {
    const changedFields = getChangedFarmFields(index, value);
    const nextFarms = farms.map((farm, farmIndex) => (farmIndex === index ? value : farm));
    setFarms(nextFarms);
    clearApiFarmErrors(index, changedFields);
    validateFarms(nextFarms);
  }

  function getChangedFarmFields(index: number, value: FarmFormValueV2) {
    const previous = farms[index];
    if (!previous) return Object.keys(value);

    const changedFields = Object.keys(value).filter(
      (field) => previous[field as keyof FarmFormValueV2] !== value[field as keyof FarmFormValueV2]
    );

    if (previous.address.state !== value.address.state) changedFields.push("address.state");
    if (previous.address.city !== value.address.city) changedFields.push("address.city");
    if (previous.season.rain_initial !== value.season.rain_initial) changedFields.push("season.rain_initial");
    if (previous.season.dry_initial !== value.season.dry_initial) changedFields.push("season.dry_initial");

    return changedFields;
  }

  function clearApiFarmErrors(index: number, changedFields: string[]) {
    if (!changedFields.length) return;

    setApiFieldErrors((current) => {
      const next = { ...current };
      Object.keys(next).forEach((field) => {
        const match = field.match(/^farms\[(\d+)\]\.(.+)$/);
        if (!match || Number(match[1]) !== index) return;
        if (changedFields.includes(mapApiFarmField(field))) delete next[field];
      });
      return next;
    });
  }

  function mapApiFarmField(field: string) {
    const fieldName = field.replace(/^farms\[\d+\]\./, "");
    const fieldMap = {
      ownerUserId: "manager_user_id",
      owner_user_id: "manager_user_id",
      state: "address.state",
      city: "address.city",
      mapCoords: "map_coords",
      map_coords: "map_coords",
    } as Record<string, string>;
    return fieldMap[fieldName] || fieldName;
  }

  function getFarmApiErrors(index: number) {
    return Object.entries(apiFieldErrors).reduce((fieldErrors, [field, message]) => {
      const match = field.match(/^farms\[(\d+)\]\.(.+)$/);
      if (!match || Number(match[1]) !== index) return fieldErrors;
      fieldErrors[mapApiFarmField(field)] = message;
      return fieldErrors;
    }, {} as ValidationErrorsV2);
  }

  function getFarmErrors(index: number) {
    return {
      ...getFarmApiErrors(index),
      ...(errors[String(index)] || {}),
    };
  }

  function hasFarmApiErrors(farmsToMatch = farms) {
    const farmsCount = Math.max(farmsToMatch.length, payload?.farms?.length || 0);

    return Object.keys(apiFieldErrors).some((field) => {
      const match = field.match(/^farms\[(\d+)\]\./);
      return !!match && Number(match[1]) < farmsCount;
    });
  }

  async function handleStateChange(index: number, state: OptionV2 | null) {
    if (!state) return;
    const data = await fetchStateCities(String(state.value));
    const farmKey = farms[index]?.uuid || String(index);
    setCitiesByFarm((current) => ({
      ...current,
      [farmKey]: data.map((city: any) => ({ label: city.nome, value: city.id })),
    }));
  }

  function addFarm() {
    const nextFarms = [...farms, createFarmBase()];
    setFarms(nextFarms);
    validateFarms(nextFarms);
  }

  function removeFarmCascade(farm: FarmFormValueV2) {
    const farmId = farm.uuid || farm.id;
    if (!farmId) return;

    setPayload((current) => {
      const removedAreaIds = (current?.areas || [])
        .filter((area: any) => String(area.farm_id) === String(farmId))
        .map((area: any) => String(area.uuid || area.id));
      const next = {
        ...current,
        farms: (current?.farms || []).filter((currentFarm: any) => String(currentFarm.uuid || currentFarm.id) !== String(farmId)),
        areas: (current?.areas || []).filter((area: any) => String(area.farm_id) !== String(farmId)),
        micro_areas: (current?.micro_areas || []).filter(
          (microArea: any) => !removedAreaIds.includes(String(microArea.area_id))
        ),
      };
      persistPayload(next);
      return next as any;
    });
  }

  function removeFarm(index: number) {
    if (farms.length <= 1) return;

    const key = farmViewKey(farms[index], index);
    removeFarmCascade(farms[index]);
    const nextFarms = farms.filter((_, farmIndex) => farmIndex !== index);
    setFarms(nextFarms);
    validateFarms(nextFarms);
    setKmlErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setKmlFileNames((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  async function handleFarmKmlChange(event: ChangeEvent<HTMLInputElement>, index: number, farm: FarmFormValueV2) {
    const key = farmViewKey(farm, index);
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".kml")) {
      setKmlErrors((current) => ({ ...current, [key]: "Selecione um arquivo KML válido." }));
      event.target.value = "";
      return;
    }

    let coords: ICoords = [];

    try {
      const response = (await readFileKML(event)) as { coords: ICoords }[] | null;
      coords = response?.[0]?.coords || [];
    } catch {
      setKmlErrors((current) => ({
        ...current,
        [key]: `Não foi possível ler coordenadas válidas do arquivo ${selectedFile.name}.`,
      }));
      event.target.value = "";
      return;
    }

    if (coords.length < 3) {
      setKmlErrors((current) => ({
        ...current,
        [key]: `Não foi possível ler coordenadas válidas do arquivo ${selectedFile.name}.`,
      }));
      event.target.value = "";
      return;
    }

    updateFarm(index, { ...farm, map_coords: coords });
    setKmlErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setKmlFileNames((current) => ({ ...current, [key]: selectedFile.name }));
    event.target.value = "";
  }

  function navigateToFirstFarmStep(attempt = 0) {
    const progress = userProgressRef.current;
    const firstFarmSection = progress.find((section) => section.section === 2);

    if (firstFarmSection?.screens?.length) {
      setShowScreen(progress, 2, 0);
      return;
    }

    if (attempt < 10) {
      setTimeout(() => navigateToFirstFarmStep(attempt + 1), 50);
    }
  }

  async function handleNext() {
    setShowFullValidation(true);
    const isValid = await validateFarms(farms, true);
    if (!isValid) return false;

    const payloadFarms = farms.map(fromFarmFormValue);
    setPayload((current) => {
      const next = { ...current, farms: payloadFarms };
      persistPayload(next);
      return next as any;
    });
    updateStateManually(userProgress, 1, 3, "advanced");
    setTimeout(() => navigateToFirstFarmStep(), 0);
    return false;
  }

  useEffect(() => {
    fetchStatesUF().then((ufs) => setStates(ufs.map((uf: string) => ({ label: uf, value: uf }))));
  }, []);

  useEffect(() => {
    axios.get("/farms/user").then(({ data }) => {
      setInitialsUsed(data.map((farm: any) => farm.initials));
    });
  }, []);

  useEffect(() => {
    if (payload?.farms?.length) {
      setFarms(payload.farms.map((farm: any) => toFarmFormValue(farm, managers, states)));
    }
  }, [payload?.farms?.length, managers.length, states.length]);

  useEffect(() => {
    const invalid = Object.values(errors).some(hasErrors);
    const hasApiErrors = hasFarmApiErrors();
    setBlockNextStep(invalid || hasApiErrors);
    if (!invalid && !hasApiErrors) clearStepError(1, 3);
  }, [apiFieldErrors, errors, farms.length, setBlockNextStep, clearStepError]);

  useEffect(() => {
    userProgressRef.current = userProgress;
  }, [userProgress]);

  return (
    <>
      <main>
        <div className="onboarding-v2-form-page">
          {farms.map((farm, index) => (
            <div className="onboarding-v2-list-card" key={farm.uuid || index}>
              <FarmFormV2
                value={farm}
                managers={managers}
                states={states}
                cities={citiesByFarm[farm.uuid || String(index)] || (farm.address.city ? [farm.address.city] : [])}
                errors={getFarmErrors(index)}
                cardTitle={`Fazenda ${index + 1}`}
                cardDescription={
                  farm.name
                    ? `${farm.name}${farm.initials ? ` (${farm.initials})` : ""}`
                    : farm.initials || undefined
                }
                showActions={false}
                headerActions={
                  farms.length > 1 ? (
                    <RemoveButtonV2 onClick={() => removeFarm(index)} />
                  ) : undefined
                }
                seasonSlot={
                  <div className="ov2-property-form__slider">
                    <SliderComponent
                      listOptions={Constants.MONTHS_SHORT_NAME}
                      rain_initial={farm.season.rain_initial}
                      dry_initial={farm.season.dry_initial}
                      setRainInitial={(rain_initial) => {
                        if (farm.season.rain_initial !== rain_initial) {
                          updateFarm(index, { ...farm, season: { ...farm.season, rain_initial } });
                        }
                      }}
                      setDryInitial={(dry_initial) => {
                        if (farm.season.dry_initial !== dry_initial) {
                          updateFarm(index, { ...farm, season: { ...farm.season, dry_initial } });
                        }
                      }}
                    />
                  </div>
                }
                mapSlot={
                  <div className="ov2-farm-map-slot">
                    {hasCoords(farm.map_coords) ? (
                      <>
                        <div className="ov2-farm-map-slot__toolbar">
                          <input
                            id={`farm-kml-${farmViewKey(farm, index)}`}
                            className="ov2-kml-upload__input"
                            type="file"
                            accept=".kml,application/vnd.google-earth.kml+xml"
                            onChange={(event) => handleFarmKmlChange(event, index, farm)}
                          />
                        </div>
                        <DrawOnMapSlotV2
                          value={getFarmMapCoords(farm.map_coords)}
                          initialsLabel={farm.initials || farm.name || `Fazenda ${index + 1}`}
                          error={getFarmErrors(index).map_coords}
                          onChange={(map_coords) => updateFarm(index, { ...farm, map_coords })}
                        />
                      </>
                    ) : (
                      <div className="ov2-kml-upload">
                        <span className="ov2-kml-upload__eyebrow">Mapa da fazenda</span>
                        <strong>Upload Mapa KML</strong>
                        <p>Selecione um arquivo KML com o polígono da fazenda para carregar o mapa.</p>
                        <label className="ov2-kml-upload__button" htmlFor={`farm-kml-${farmViewKey(farm, index)}`}>
                          Selecionar KML
                        </label>
                        <input
                          id={`farm-kml-${farmViewKey(farm, index)}`}
                          className="ov2-kml-upload__input"
                          type="file"
                          accept=".kml,application/vnd.google-earth.kml+xml"
                          onChange={(event) => handleFarmKmlChange(event, index, farm)}
                        />
                        {(kmlErrors[farmViewKey(farm, index)] || getFarmErrors(index).map_coords) && (
                          <small className="ov2-field__error animation__fadeIn">
                            {formatErrorMessageV2(kmlErrors[farmViewKey(farm, index)] || getFarmErrors(index).map_coords)}
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                }
                onStateChange={(state) => handleStateChange(index, state)}
                onBlur={() => validateFarms()}
                onChange={(value) => updateFarm(index, value)}
              />
            </div>
          ))}
          <div className="onboarding-v2-page-actions">
            <ButtonV2 onClick={addFarm}>+ ADICIONAR FAZENDA</ButtonV2>
          </div>
        </div>
      </main>
      <OnboardingFooterV2 nextStepEvent={handleNext} />
    </>
  );
}
